<template>
  <Modal @hide="reset">
    <template #activator="{ show }">
      <slot :show="show" />
    </template>
    <template #header="{ hide }">
      <div class="flex justify-between items-center w-full">
        <div class="flex gap-2 text-blue-800 items-center">
          <EvidenceLogo class="w-7" />
          <p class="font-semibold">Evidence Safety Information Database</p>
        </div>
        <button class="p-2" @click="hide">
          <XMarkIcon class="w-6 text-slate-400 hover:text-slate-600" />
        </button>
      </div>
    </template>
    <template #content>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <span class="text-sm font-medium text-slate-700">Search Mode:</span>
          <div class="flex bg-slate-100 rounded-lg p-1">
            <button
              v-for="mode in searchModes"
              :key="mode.value"
              class="px-3 py-1.5 text-sm font-medium rounded transition-all duration-200"
              :class="[
                searchMode === mode.value
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900',
              ]"
              @click="searchMode = mode.value"
            >
              {{ mode.label }}
            </button>
          </div>
        </div>
      </div>
      <div class="w-[980px] create-review-modal flex flex-col rounded-2xl relative">
        <div
          v-if="isSearching || loading.isLoading.value"
          class="overflow-hidden absolute top-0 left-0 w-full h-1"
        >
          <div class="line absolute bg-primary-500/20 h-1"></div>
          <div class="absolute bg-primary-500 h-1 inc"></div>
          <div class="absolute bg-primary-500 h-1 dec"></div>
        </div>
        <div class="px-6 mt-2 space-y-4">
          <div v-if="searchMode === 'raw'" class="space-y-2">
            <label class="block text-sm font-medium text-slate-700"> Query </label>
            <TextInput
              v-model="rawQuery"
              placeholder="e.g. pacemaker AND medtronic"
              class="w-full"
              :input-classes="'px-3 py-2 text-sm'"
              :disabled="isSearching"
              @enter="onEnter"
            >
            </TextInput>
          </div>
          <div v-else class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="block text-sm font-medium text-slate-700"> Device Name </label>
                <TextInput
                  v-model="device"
                  placeholder="Enter device name"
                  class="w-full"
                  :input-classes="'px-3 py-2 text-sm'"
                  :disabled="isSearching"
                  @enter="onEnter"
                />
              </div>
              <div class="space-y-2">
                <label class="block text-sm font-medium text-slate-700"> Manufacturer </label>
                <TextInput
                  v-model="manufacturer"
                  placeholder="e.g., Medtronic, Boston Scientific"
                  class="w-full"
                  :input-classes="'px-3 py-2 text-sm'"
                  :disabled="isSearching"
                  @enter="onEnter"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="relative">
          <div class="mx-6 my-4 bg-white rounded-lg space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <DatePicker v-model="startDate" placeholder="Start Date" class="flex-1" />
              <DatePicker v-model="endDate" placeholder="End Date" class="flex-1" />
            </div>
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-slate-700">Sources</h3>
                <button
                  type="button"
                  class="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors duration-200"
                  @click="showDetailedSelection = !showDetailedSelection"
                >
                  {{ showDetailedSelection ? "Detailed" : "Simple" }}
                </button>
              </div>
              <SafetyInformationSourcesSelection
                v-model="selectedSafetyInformationSources"
                :show-detailed-selection="showDetailedSelection"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer="{ hide }">
      <div class="p-6 flex justify-between items-center w-full">
        <div />
        <div>
          <button
            class="truncate flex flex-row gap-2 bg-blue-800 text-white items-center py-2 px-4 rounded-md text-base font-medium"
            :class="{
              '!bg-slate-100 !text-black/50': !canImport || isSearching || loading.isLoading.value,
            }"
            :disabled="!canImport || isSearching || loading.isLoading.value"
            @click="
              () =>
                importSafetyInformationSearches(selectedSafetyInformationSources).then(() => hide())
            "
          >
            <ImportIcon
              class="w-5 h-5 text-white"
              :class="{
                '!text-black/50': !canImport || isSearching || loading.isLoading.value,
              }"
            />

            <span> Import {{ totalResults > -1 ? totalResults : 0 }} results </span>
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import DatePicker from "@app/components/Global/Inputs/DatePicker.vue";
import { computed, ref } from "vue";
import { SnackbarState } from "@app/types";
import useSnackbar from "@app/composables/use-snackbar";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import { ImportSourceType } from "@core/domain/types/import-source-type.type";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";
import axios from "axios";
import ImportIcon from "@app/components/Icons/ImportIcon.vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import Modal from "@app/components/Global/Modal/Modal.vue";
import { SafetyInformationApiClientKey, SafetyInformationSourcesKey } from "@app/injectionKeys";
import SafetyInformationSourcesSelection from "@app/components/SafetyInformationSourcesSelection.vue";
import { Review } from "@core/domain/models/review.model";
import { FieldSafetyNoticeMetadata } from "@core/domain/models/reviewItemMetadata/fieldSafetyNoticeMetadata";
import { Prettify } from "@app/utils/prettifyType";
import { ReviewItemType } from "@core/domain/types/reviewItemType.type";
import useLoading from "@app/composables/use-loading";
import EvidenceLogo from "@app/components/Icons/EvidenceLogo.vue";
import { watchDebounced } from "@vueuse/core";

const safetyInformationSources = injectStrict(SafetyInformationSourcesKey);
const selectedSafetyInformationSources = ref<string[]>(
  safetyInformationSources.map((source) => source.id),
);
const showDetailedSelection = ref(false);

const safetyInformationApiClient = injectStrict(SafetyInformationApiClientKey);

const review = injectStrict(ReviewKey);
const snackbar = useSnackbar();

const results = ref<FieldSafetyNoticeMetadata[]>([]);
const currentPage = ref(1);
const totalPages = ref(1);
const limit = ref(12);
const totalResults = ref(-1);

const isSearching = ref(false);
const loading = useLoading();
const startDate = ref("");
const endDate = ref("");

type SearchMode = "structured" | "raw";
const searchMode = ref<SearchMode>("structured");
const rawQuery = ref("");
const device = ref("");
const manufacturer = ref("");

const searchModes = ref([
  { value: "structured" as SearchMode, label: "Device/Manufacturer" },
  { value: "raw" as SearchMode, label: "Query" },
]);

const searchExpression = computed(() => {
  if (searchMode.value === "raw") {
    return rawQuery.value.trim();
  }

  const deviceValue = device.value.trim();
  const manufacturerValue = manufacturer.value.trim();

  if (deviceValue && manufacturerValue) {
    return `${deviceValue} AND ${manufacturerValue}`;
  } else if (deviceValue) {
    return deviceValue;
  } else if (manufacturerValue) {
    return manufacturerValue;
  }
  return "";
});

const canSearch = computed(() => {
  return searchExpression.value !== "" && selectedSafetyInformationSources.value.length > 0;
});

const canImport = computed(() => {
  return review.entity.value?.plan?.lockState === ReviewLockState.UNLOCKED && canSearch.value;
});

async function importSafetyInformationSearches(safetyInformationSourceIds: string[]) {
  loading.start();
  const sourceIds = [...safetyInformationSourceIds];
  if (sourceIds.length === 0) {
    sourceIds.push(...safetyInformationSources.map((source) => source.id));
  }
  try {
    const safetyInformationBySource = new Map<string, FieldSafetyNoticeMetadata[]>();
    safetyInformationSourceIds.forEach((source) => {
      safetyInformationBySource.set(source, []);
    });
    const { results: searchResults } = await safetyInformationApiClient.search({
      query: searchExpression.value,
      sources: sourceIds,
      filters: {
        startDate: startDate.value,
        endDate: endDate.value,
      },
    });
    searchResults.forEach((item) => {
      const source = item.source!;
      safetyInformationBySource.get(source)?.push(item);
    });
    const searches: {
      query?: string;
      filters?: string;
      date: string;
      reviewId: number;
      importSourceId: string;
      items: Prettify<FieldSafetyNoticeMetadata & { type: ReviewItemType.FieldSafetyNotice }>[];
      url?: string;
    }[] = [];
    for (const [source, items] of safetyInformationBySource) {
      const safetyInformationSource = safetyInformationSources.find((s) => s.id === source);
      const appliedFilter = safetyInformationSource?.appliedFilter;
      let dateFilter = "";
      if (startDate.value && endDate.value) {
        dateFilter += `Date: from ${startDate.value} to ${endDate.value}`;
      } else if (startDate.value) {
        dateFilter += `Date: from ${startDate.value}`;
      } else if (endDate.value) {
        dateFilter += `Date: to ${endDate.value}`;
      }

      searches.push({
        query: searchExpression.value,
        date: new Date().toISOString(),
        reviewId: review.entity.value.id,
        importSourceId: source,
        filters: [appliedFilter, dateFilter].filter((f) => f).join(", "),
        items: items.map((a) => ({
          ...a,
          type: ReviewItemType.FieldSafetyNotice,
        })),
      });
    }
    for await (const search of searches) {
      if (!sourceExistsInPlan(search.importSourceId, review.entity.value)) {
        const safetyInformationSource = safetyInformationSources.find(
          (s) => s.id === search.importSourceId,
        );
        if (safetyInformationSource) {
          await review.addImportSourceToPlan({
            name: safetyInformationSource.name,
            type: ImportSourceType.FIELD_SAFETY_NOTICES,
            url: safetyInformationSource.url,
            id: safetyInformationSource.id,
            description: safetyInformationSource.description,
          });
        }
      }

      await review.importSearch(search);
    }
    reset();
  } catch (error) {
    if (axios.isAxiosError(error) && error.response && error.response.status === 500) {
      snackbar.show(SnackbarState.ERROR, "Internal Server Error. Please try again.");
    } else {
      snackbar.show(SnackbarState.ERROR, "Failed to import safety information.");
    }
    console.error("Error importing safety information:", error);
  } finally {
    loading.stop();
  }
}

function sourceExistsInPlan(importSourceId: string, review: Review): boolean {
  const enabledSources = review.plan?.importPlan.importSources || [];
  return enabledSources.some((source) => source.id === importSourceId);
}

async function search(
  params: { limit: number; offset: number },
  expression = searchExpression.value,
) {
  if (!expression) return;

  isSearching.value = true;
  try {
    const { results: safetyInformationRecords, totalFound } =
      await safetyInformationApiClient.search({
        pagination: params,
        query: expression,
        sources: selectedSafetyInformationSources.value,
        filters: {
          startDate: startDate.value,
          endDate: endDate.value,
        },
      });

    if (safetyInformationRecords.length) {
      results.value = safetyInformationRecords;
      totalResults.value = totalFound;
      totalPages.value = Math.ceil(totalResults.value / limit.value);
    } else {
      results.value = [];
      totalResults.value = 0;
      totalPages.value = 1;
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response && error.response.status === 500) {
      snackbar.show(SnackbarState.ERROR, "Internal Server Error. Please try again.");
    }
    console.error("Error fetching safety information:", error);
    results.value = [];
    totalResults.value = 0;
    totalPages.value = 1;
  } finally {
    isSearching.value = false;
  }
}

function onEnter() {
  search({ limit: limit.value, offset: (currentPage.value - 1) * limit.value });
}

watchDebounced(
  () => [
    selectedSafetyInformationSources.value.slice().sort().join(","),
    startDate.value,
    endDate.value,
    searchExpression.value,
  ],
  () => {
    if (!canSearch.value) {
      results.value = [];
      totalResults.value = -1;
      return;
    }

    currentPage.value = 1;
    search({
      limit: limit.value,
      offset: 0,
    });
  },
  { debounce: 800 },
);

function reset() {
  results.value = [];
  currentPage.value = 1;
  totalPages.value = 1;
  totalResults.value = -1;
  startDate.value = "";
  endDate.value = "";
  selectedSafetyInformationSources.value = safetyInformationSources.map((source) => source.id);
  device.value = "";
  manufacturer.value = "";
  rawQuery.value = "";
  searchMode.value = "structured";
}
</script>
